import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
} from '@/components/DataTable';
import {
  listOrgExtensionsDataTable,
  getOrgExtensionFilterOptions,
} from '@kirin/api-client/api/extensions';
import { listOrganizationMembers } from '@kirin/api-client/api/members';
import type {
  ExtensionListItem,
  OrgExtensionFilterableField,
  OrgExtensionFilterOptionField,
  OrgExtensionSortField,
} from '@kirin/api-client/contracts/extensions';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';

/**
 * Fetch org extensions with DataTable pagination, filters, and sorting.
 */
export async function fetchOrgExtensions(
  organizationId: string,
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<DataFetchResponse<ExtensionListItem>> {
  return listOrgExtensionsDataTable(
    apiClient,
    organizationId,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field as OrgExtensionSortField | undefined,
      sortOrder: params.sort?.order,
      search: params.search,
      knownTotal: params.knownTotal,
    },
    { signal }
  );
}

/**
 * Fetch distinct filter option values for an org extension column.
 * Returns paginated result shape to support infinite-scroll in filter dropdowns.
 */
export async function fetchOrgExtensionFilterOptions(
  organizationId: string,
  field: OrgExtensionFilterableField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getOrgExtensionFilterOptions(
    apiClient,
    organizationId,
    {
      field: field as OrgExtensionFilterOptionField,
      search: params.search,
      limit: params.limit,
      offset: params.offset,
    }
  );
  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset,
    hasMore: response.pagination.hasMore,
  };
}

/**
 * Build the React Query key for an org extension filter options request.
 */
export function getOrgExtensionFilterOptionsQueryKey(
  organizationId: string,
  field: OrgExtensionFilterableField,
  params: { search?: string; limit?: number }
): readonly unknown[] {
  return queryKeys.extensions.orgFilterOptions(organizationId, field, params);
}

/**
 * Fetch org members as filter options for the Users column.
 * Uses the members API with client-side search — the same pattern as the
 * org alerts Users filter. Returns options shaped for the DataTable render prop:
 * { value: internalUserId, label: name|email, metadata: { id, name, email } }
 */
export async function fetchOrgExtensionUserFilterOptions(
  organizationId: string,
  params: FilterOptionsParams
): Promise<
  Array<{ value: string; label: string; metadata: Record<string, unknown> }>
> {
  const members = await listOrganizationMembers(apiClient, organizationId);

  let filtered = members
    // KIR-3908: only real accounts. Discovered/Unattributed rows never have
    // installed extensions to filter on, and the Unattributed row has no
    // name/email — offering it would leak its raw internal UUID as the label.
    .filter(m => m.internalId && (!m.userType || m.userType === 'full'))
    .map(m => ({ id: m.internalId as string, name: m.name, email: m.email }));

  if (params.search) {
    const q = params.search.toLowerCase();
    filtered = filtered.filter(
      m =>
        m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q)
    );
  }

  return filtered.map(m => ({
    value: m.id,
    label: m.name || m.email || m.id,
    metadata: { id: m.id, name: m.name, email: m.email },
  }));
}

/**
 * Query key for org extension user filter options.
 * Keyed under members (not extensions/filter-options) since the source is
 * the members API, not the extensions filter-options endpoint.
 */
export function getOrgExtensionUserFilterOptionsQueryKey(
  organizationId: string,
  params: FilterOptionsParams
): string[] {
  return [
    'organizations',
    organizationId,
    'members',
    'filter-options',
    ...(params.search ? [params.search] : []),
  ];
}
